import type * as React from 'react';
import { type FC, useEffect, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';
import type { CountryCode } from '@crehana/ts-types';
// biome-ignore lint/style/noRestrictedImports: legacy import kept for compatibility
import { Tab, Tabs, TextTag } from '@crehana/ui';
import { useMe, useUITheme } from '@crehana/web';
import { clsx } from 'clsx';
import {
  Route,
  Switch,
  useHistory,
  useLocation,
  useRouteMatch,
  // biome-ignore lint/style/noRestrictedImports: legacy import kept for compatibility
} from 'react-router-dom';

import { useCrehanaTranslation } from '@/views/UserDashboard/hooks';

import {
  ENROLLMENTS_ACADEMIES_ROUTE,
  ENROLLMENTS_BOOTCAMPS_ROUTE,
  ENROLLMENTS_BUNDLES_ROUTE,
  ENROLLMENTS_CAREERS_ROUTE,
  ENROLLMENTS_COURSES_ROUTE,
  ENROLLMENTS_DEGREES_ROUTE,
  ENROLLMENTS_ROUTES,
  ENROLLMENTS_TRACKS_ROUTE,
} from '../route-paths';
import LazyGetLeadBannerSnackBar from './components/GetLeadBannerSnackBar';
import GET_USER_PERMISSIONS_QUERY from './graphql/GetUserDashboardPermissions.graphql';
import type { GetUserDashboardPermissions as GetUserDashboardPermissionsResponse } from './graphql/types/GetUserDashboardPermissions';
import type { UserDashboardAcademiesPermissionQuery } from './graphql/types/UserDashboardAcademiesPermissionQuery';
import GET_USER_ACADEMIES_PERMISSIONS_QUERY from './graphql/UserDashboardAcademiesPermissionQuery.v4.graphql';
import Academies from './sections/Academies';
import Bootcamps from './sections/Bootcamps';
import Bundles from './sections/Bundles';
import Careers from './sections/Careers';
import Courses from './sections/Courses';
import Degrees from './sections/Degrees';
import Tracks from './sections/Tracks';

type NestedRoute = {
  key: string;
  path: string;
  label?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: React.ComponentType<React.PropsWithChildren<any>>;
  children?: React.ReactNode;
};

interface EnrollmentsProps {
  countryPrefix?: CountryCode | string;
}

const Enrollments: FC<React.PropsWithChildren<EnrollmentsProps>> = ({
  countryPrefix,
}) => {
  const t = useCrehanaTranslation();
  const location = useLocation();
  const history = useHistory();
  const match = useRouteMatch();

  const { isDark } = useUITheme();
  const { me } = useMe();

  const isFreeTrialUser = me?.membership?.membershipType?.method === 'A_2';
  const showComponentToGetLeadPhone = isFreeTrialUser && countryPrefix !== 'br';
  const [showComponentLead, setShowComponentLead] = useState(
    showComponentToGetLeadPhone,
  );

  const [permissionsRetries, setPermissionsRetries] = useState<number>(0);
  const { loading, data, error, refetch } =
    useQuery<GetUserDashboardPermissionsResponse>(GET_USER_PERMISSIONS_QUERY);
  const { data: academiesData } =
    useQuery<UserDashboardAcademiesPermissionQuery>(
      GET_USER_ACADEMIES_PERMISSIONS_QUERY,
      { context: { clientName: 'v4' } },
    );

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentionally managed
  useEffect(() => {
    if (!loading && (error || !data) && permissionsRetries < 3) {
      setPermissionsRetries(permissionsRetries + 1);
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, data, error]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentionally managed
  const nestedRoutes = useMemo(() => {
    const nested: NestedRoute[] = [
      {
        ...ENROLLMENTS_COURSES_ROUTE,
        label: t('UD_ENROLLMENTS_COURSES'),
        Component: Courses,
      },
    ];
    const hasTracks = !!data?.meTracks?.edges.length;
    const hasCareers = !!data?.meCareers?.edges.length;
    const hasBundles = !!data?.me?.hasCertificationEnrolled;
    const hasDegrees = !!me?.userDegreeSegmentation;
    const hasAcademies =
      !!academiesData?.enrolledEducationAcademies.edges.length;
    const hasBootcamps = !!data?.me?.hasBootcamps;

    if (hasDegrees) {
      nested.push({
        ...ENROLLMENTS_DEGREES_ROUTE,
        label: t('UD_ENROLLMENTS_DEGREES'),
        Component: Degrees,
      });
    }
    if (hasBootcamps) {
      nested.push({
        ...ENROLLMENTS_BOOTCAMPS_ROUTE,
        label: t('UD_ENROLLMENTS_BOOTCAMPS'),
        Component: Bootcamps,
      });
    }
    if (hasAcademies) {
      nested.push({
        ...ENROLLMENTS_ACADEMIES_ROUTE,
        children: (
          <span>
            {t('UD_ENROLLMENTS_ACADEMIES')}
            <TextTag
              label={t('UD_ENROLLMENTS_NEW_TAG')}
              type="green"
              className={clsx('ml-8', {
                'bg-secondary-light': isDark,
              })}
            />
          </span>
        ),
        Component: Academies,
      });
    }
    if (hasTracks) {
      nested.push({
        ...ENROLLMENTS_TRACKS_ROUTE,
        label: t('UD_ENROLLMENTS_TRACKS'),
        Component: Tracks,
      });
    }
    if (hasCareers) {
      nested.push({
        ...ENROLLMENTS_CAREERS_ROUTE,
        label: t('UD_ENROLLMENTS_CAREERS'),
        Component: Careers,
      });
    }
    if (hasBundles) {
      nested.push({
        ...ENROLLMENTS_BUNDLES_ROUTE,
        label: t('UD_ENROLLMENTS_BUNDLES'),
        Component: Bundles,
      });
    }
    return nested;
  }, [data, academiesData, isDark]);
  const [selectedTab, setSelectedTab] = useState<number | string>(0);

  useEffect(() => {
    const path = location.pathname.split('/')[2];
    const currentRoute = ENROLLMENTS_ROUTES.find(
      r => r.path.replace(/\//g, '') === path,
    );

    setSelectedTab(currentRoute?.key || '');
  }, [location.pathname]);

  const onTabChange = (value: number | string) => {
    setSelectedTab(value);
    const currentRoute = ENROLLMENTS_ROUTES.find(r => r.key === value);

    history.push(`${match.path}${currentRoute?.path || ''}`);
  };

  return (
    <>
      {nestedRoutes.length > 1 && (
        <Tabs
          className="mb-24"
          backgroundColor={isDark ? 'base-dark' : 'gray-lighter'}
          value={selectedTab}
          onTabChange={onTabChange}
          dark={isDark}
          withGridContainer="dashboard"
          full
          scrollable
          sticky
        >
          {nestedRoutes.map(route => (
            <Tab
              {...route}
              key={`${route.key}-tab`}
              label={route.label}
              value={route.key}
            />
          ))}
        </Tabs>
      )}

      {showComponentLead && (
        <LazyGetLeadBannerSnackBar
          hideDialog={() => setShowComponentLead(false)}
        />
      )}
      <Switch>
        {nestedRoutes.slice(1).map(({ key, path, Component }) => (
          <Route key={key} path={`${match.path}${path}`}>
            {/* @ts-ignore */}
            <Component me={me!} isActiveCourses />
          </Route>
        ))}
        {/* Default route */}
        <Route path={`${match.path}${ENROLLMENTS_COURSES_ROUTE.path}`}>
          <Courses me={me!} countryPrefix={countryPrefix} isActiveCourses />
        </Route>
      </Switch>
    </>
  );
};

export default Enrollments;
